import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/card";
import { Button } from "../components/button";
import { Progress } from "../components/progress";
import { Users, Lightbulb, Brain, Gauge, Briefcase, CheckCircle2, FileText, RotateCcw, Sparkles, AlertTriangle, Target, Heart, MessageSquare, BarChart3, Check, TrendingUp, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { toast } from "react-toastify";
import api from "../sever/Api";
import { generateAIReport } from "../sever/Api";
import { notifyTestCompletion } from "../lib/testUtils";

// MBTI Test Data
const mbtiSections = [
  {
    name: "Extraversion  ",
    description: "How you gain energy: from interaction (E) or reflection (I)",
    icon: Users,
    color: "from-blue-500 to-cyan-600",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    borderColor: "border-blue-200",
    questions: [
      {
        id: "eivsi-q1",
        prompt: "At a party, I usually:",
        statements: [
          { id: "eivsi-q1-a", text: "Talk to as many people as possible", letter: "E" },
          { id: "eivsi-q1-b", text: "Chat with a few close friends", letter: "I" },
          { id: "eivsi-q1-c", text: "Mingle slowly and observe first", letter: "I" },
          { id: "eivsi-q1-d", text: "Prefer a quiet corner", letter: "I" },
        ],
      },
      {
        id: "eivsi-q2",
        prompt: "When I need to recharge, I:",
        statements: [
          { id: "eivsi-q2-a", text: "Spend time with people", letter: "E" },
          { id: "eivsi-q2-b", text: "Call a friend or family member", letter: "E" },
          { id: "eivsi-q2-c", text: "Do a mix of social and alone time", letter: "I" },
          { id: "eivsi-q2-d", text: "Spend time alone", letter: "I" },
        ],
      },
      {
        id: "eivsi-q3",
        prompt: "In group work, I:",
        statements: [
          { id: "eivsi-q3-a", text: "Take the lead", letter: "E" },
          { id: "eivsi-q3-b", text: "Support others actively", letter: "E" },
          { id: "eivsi-q3-c", text: "Contribute when necessary", letter: "I" },
          { id: "eivsi-q3-d", text: "Prefer working alone", letter: "I" },
        ],
      },
      {
        id: "eivsi-q4",
        prompt: "I make decisions best when:",
        statements: [
          { id: "eivsi-q4-a", text: "Talking them through with others", letter: "E" },
          { id: "eivsi-q4-b", text: "Discussing with a trusted person", letter: "E" },
          { id: "eivsi-q4-c", text: "Thinking alone for a while", letter: "I" },
          { id: "eivsi-q4-d", text: "Reflecting quietly without input", letter: "I" },
        ],
      },
      {
        id: "eivsi-q5",
        prompt: "In conversation, I:",
        statements: [
          { id: "eivsi-q5-a", text: "Speak more than I listen", letter: "E" },
          { id: "eivsi-q5-b", text: "Balance speaking and listening", letter: "E" },
          { id: "eivsi-q5-c", text: "Listen more than I speak", letter: "I" },
          { id: "eivsi-q5-d", text: "Mostly stay silent and observe", letter: "I" },
        ],
      },
    ],
  },
  {
    name: "Sensing ",
    description: "How you take in information: concrete (S) or conceptual (N)",
    icon: Lightbulb,
    color: "from-emerald-500 to-green-600",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-700",
    borderColor: "border-emerald-200",
    questions: [
      {
        id: "svn-q6",
        prompt: "I remember:",
        statements: [
          { id: "svn-q6-a", text: "Exact facts and details", letter: "S" },
          { id: "svn-q6-b", text: "Most details, but also the big picture", letter: "S" },
          { id: "svn-q6-c", text: "The big picture more than details", letter: "N" },
          { id: "svn-q6-d", text: "Ideas and impressions", letter: "N" },
        ],
      },
      {
        id: "svn-q7",
        prompt: "I trust:",
        statements: [
          { id: "svn-q7-a", text: "What I can see and measure", letter: "S" },
          { id: "svn-q7-b", text: "Facts supported by logic", letter: "S" },
          { id: "svn-q7-c", text: "Possibilities and future trends", letter: "N" },
          { id: "svn-q7-d", text: "My imagination and gut", letter: "N" },
        ],
      },
      {
        id: "svn-q8",
        prompt: "I prefer learning:",
        statements: [
          { id: "svn-q8-a", text: "Step-by-step instructions", letter: "S" },
          { id: "svn-q8-b", text: "Practical examples", letter: "S" },
          { id: "svn-q8-c", text: "Concepts and theories", letter: "N" },
          { id: "svn-q8-d", text: "Open-ended exploration", letter: "N" },
        ],
      },
      {
        id: "svn-q9",
        prompt: "When solving a problem, I:",
        statements: [
          { id: "svn-q9-a", text: "Follow proven methods", letter: "S" },
          { id: "svn-q9-b", text: "Check past experiences", letter: "S" },
          { id: "svn-q9-c", text: "Think of new possibilities", letter: "N" },
          { id: "svn-q9-d", text: "Imagine creative solutions", letter: "N" },
        ],
      },
      {
        id: "svn-q10",
        prompt: "I enjoy conversations about:",
        statements: [
          { id: "svn-q10-a", text: "Real-world events", letter: "S" },
          { id: "svn-q10-b", text: "Practical tips", letter: "S" },
          { id: "svn-q10-c", text: "Big ideas and possibilities", letter: "N" },
          { id: "svn-q10-d", text: "Future innovations", letter: "N" },
        ],
      },
    ],
  },
  {
    name: "Thinking (T) / Feeling (F)",
    description: "How you decide: objective logic (T) or people-centered values (F)",
    icon: Brain,
    color: "from-purple-500 to-violet-600",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    borderColor: "border-purple-200",
    questions: [
      {
        id: "tvf-q11",
        prompt: "I make decisions based on:",
        statements: [
          { id: "tvf-q11-a", text: "Logic and fairness", letter: "T" },
          { id: "tvf-q11-b", text: "Evidence and consistency", letter: "T" },
          { id: "tvf-q11-c", text: "People’s needs and feelings", letter: "F" },
          { id: "tvf-q11-d", text: "Maintaining harmony", letter: "F" },
        ],
      },
      {
        id: "tvf-q12",
        prompt: "In disagreements, I focus on:",
        statements: [
          { id: "tvf-q12-a", text: "What’s logical", letter: "T" },
          { id: "tvf-q12-b", text: "The facts of the matter", letter: "T" },
          { id: "tvf-q12-c", text: "The impact on people", letter: "F" },
          { id: "tvf-q12-d", text: "Restoring peace", letter: "F" },
        ],
      },
      {
        id: "tvf-q13",
        prompt: "I value more:",
        statements: [
          { id: "tvf-q13-a", text: "Truth over harmony", letter: "T" },
          { id: "tvf-q13-b", text: "Rules over exceptions", letter: "T" },
          { id: "tvf-q13-c", text: "Harmony over truth", letter: "F" },
          { id: "tvf-q13-d", text: "Exceptions over rules", letter: "F" },
        ],
      },
      {
        id: "tvf-q14",
        prompt: "When giving feedback, I am:",
        statements: [
          { id: "tvf-q14-a", text: "Direct and honest", letter: "T" },
          { id: "tvf-q14-b", text: "Fair but firm", letter: "T" },
          { id: "tvf-q14-c", text: "Gentle and considerate", letter: "F" },
          { id: "tvf-q14-d", text: "Positive and encouraging", letter: "F" },
        ],
      },
      {
        id: "tvf-q15",
        prompt: "My friends would describe me as:",
        statements: [
          { id: "tvf-q15-a", text: "Objective", letter: "T" },
          { id: "tvf-q15-b", text: "Analytical", letter: "T" },
          { id: "tvf-q15-c", text: "Empathetic", letter: "F" },
          { id: "tvf-q15-d", text: "Supportive", letter: "F" },
        ],
      },
    ],
  },
  {
    name: "Judging",
    description: "How you approach life: planned (J) or adaptable (P)",
    icon: Gauge,
    color: "from-pink-500 to-rose-600",
    bgColor: "bg-pink-50",
    textColor: "text-pink-700",
    borderColor: "border-pink-200",
    questions: [
      {
        id: "jvp-q16",
        prompt: "My daily routine is:",
        statements: [
          { id: "jvp-q16-a", text: "Structured and consistent", letter: "J" },
          { id: "jvp-q16-b", text: "Mostly planned", letter: "J" },
          { id: "jvp-q16-c", text: "Flexible but guided", letter: "P" },
          { id: "jvp-q16-d", text: "Spontaneous", letter: "P" },
        ],
      },
      {
        id: "jvp-q17",
        prompt: "Before a trip, I:",
        statements: [
          { id: "jvp-q17-a", text: "Plan every detail", letter: "J" },
          { id: "jvp-q17-b", text: "Prepare most things in advance", letter: "J" },
          { id: "jvp-q17-c", text: "Have a loose plan", letter: "P" },
          { id: "jvp-q17-d", text: "Decide as I go", letter: "P" },
        ],
      },
      {
        id: "jvp-q18",
        prompt: "I feel best when:",
        statements: [
          { id: "jvp-q18-a", text: "Tasks are completed early", letter: "J" },
          { id: "jvp-q18-b", text: "I follow a timeline", letter: "J" },
          { id: "jvp-q18-c", text: "I can adjust deadlines", letter: "P" },
          { id: "jvp-q18-d", text: "I work last-minute", letter: "P" },
        ],
      },
      {
        id: "jvp-q19",
        prompt: "I prefer projects to be:",
        statements: [
          { id: "jvp-q19-a", text: "Organized start to finish", letter: "J" },
          { id: "jvp-q19-b", text: "Clear but flexible", letter: "J" },
          { id: "jvp-q19-c", text: "Open to changes", letter: "P" },
          { id: "jvp-q19-d", text: "Fully unstructured", letter: "P" },
        ],
      },
      {
        id: "jvp-q20",
        prompt: "My workspace is:",
        statements: [
          { id: "jvp-q20-a", text: "Neat and orderly", letter: "J" },
          { id: "jvp-q20-b", text: "Mostly organized", letter: "J" },
          { id: "jvp-q20-c", text: "A mix of tidy and messy", letter: "P" },
          { id: "jvp-q20-d", text: "Messy but functional", letter: "P" },
        ],
      },
    ],
  },
  {
    name: "Lifestyle ",
    description: "Your preferred way of working and organizing tasks",
    icon: Briefcase,
    color: "from-amber-500 to-orange-600",
    bgColor: "bg-amber-50",
    textColor: "text-amber-700",
    borderColor: "border-amber-200",
    questions: [
      {
        id: "life-q21",
        prompt: "At work, I:",
        statements: [
          { id: "life-q21-a", text: "Stick to my schedule", letter: "J" },
          { id: "life-q21-b", text: "Follow a general plan", letter: "J" },
          { id: "life-q21-c", text: "Work based on mood", letter: "P" },
          { id: "life-q21-d", text: "Take tasks as they come", letter: "P" },
        ],
      },
      {
        id: "life-q22",
        prompt: "I enjoy jobs that:",
        statements: [
          { id: "life-q22-a", text: "Have clear rules and structure", letter: "J" },
          { id: "life-q22-b", text: "Allow some creativity", letter: "J" },
          { id: "life-q22-c", text: "Are mostly free-form", letter: "P" },
          { id: "life-q22-d", text: "Have constant variety", letter: "P" },
        ],
      },
      {
        id: "life-q23",
        prompt: "In a team project, I:",
        statements: [
          { id: "life-q23-a", text: "Keep tasks on track", letter: "J" },
          { id: "life-q23-b", text: "Offer support where needed", letter: "J" },
          { id: "life-q23-c", text: "Add creative ideas", letter: "P" },
          { id: "life-q23-d", text: "Adapt to whatever role is open", letter: "P" },
        ],
      },
      {
        id: "life-q24",
        prompt: "I am most productive:",
        statements: [
          { id: "life-q24-a", text: "In the morning with a plan", letter: "J" },
          { id: "life-q24-b", text: "In the morning with flexibility", letter: "J" },
          { id: "life-q24-c", text: "At night with ideas flowing", letter: "P" },
          { id: "life-q24-d", text: "At night when deadlines push me", letter: "P" },
        ],
      },
      {
        id: "life-q25",
        prompt: "My ideal work style is:",
        statements: [
          { id: "life-q25-a", text: "Clear goals and processes", letter: "J" },
          { id: "life-q25-b", text: "Goals with flexible steps", letter: "J" },
          { id: "life-q25-c", text: "Freedom with occasional check-ins", letter: "P" },
          { id: "life-q25-d", text: "Complete independence", letter: "P" },
        ],
      },
    ],
  },
];

export default function MbtiTest() {
  const location = useLocation();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [resultsSaved, setResultsSaved] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiReportError, setAiReportError] = useState(null);
  const [aiReportText, setAiReportText] = useState("");
  const [showIntro, setShowIntro] = useState(true);
  const [autoNext, setAutoNext] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  const currentCategory = mbtiSections[currentSectionIndex];
  const currentQuestion = currentCategory.questions[currentQuestionIndex];
  const totalSections = mbtiSections.length;

  const handleAnswerChange = (questionId, statementId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        [statementId]: value,
      },
    }));
    setAutoNext(true);
  };

  useEffect(() => {
    if (autoNext) {
      if (!isLastQuestion) {
        handleNext();
      } else {
        setShowResults(true);
      }
      setAutoNext(false);
    }
    // eslint-disable-next-line
  }, [autoNext]);

  const totalQuestions = useMemo(() => mbtiSections.reduce((acc, s) => acc + s.questions.length, 0), []);

  const answeredQuestionsCount = useMemo(() => {
    let count = 0;
    mbtiSections.forEach((section) => {
      section.questions.forEach((q) => {
        if (answers[q.id] && Object.values(answers[q.id]).some((v) => v === "selected")) count++;
      });
    });
    return count;
  }, [answers]);

  const currentSectionAnsweredCount = useMemo(() => {
    let count = 0;
    currentCategory.questions.forEach((q) => {
      if (answers[q.id] && Object.values(answers[q.id]).some((v) => v === "selected")) count++;
    });
    return count;
  }, [answers, currentCategory]);

  const sectionScores = useMemo(() => {
    return mbtiSections.map((section) => {
      let count = 0;
      section.questions.forEach((q) => {
        if (answers[q.id] && Object.values(answers[q.id]).some((v) => v === "selected")) count++;
      });
      return count;
    });
  }, [answers]);

  const canGoNext = currentSectionIndex < totalSections - 1 || currentQuestionIndex < currentCategory.questions.length - 1;
  const canGoPrevious = currentSectionIndex > 0 || currentQuestionIndex > 0;
  const isLastQuestion = currentSectionIndex === totalSections - 1 && currentQuestionIndex === currentCategory.questions.length - 1;

  // Count how many statements belong to each letter
  const maxScoreByLetter = useMemo(() => {
    const counts = { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 };
    mbtiSections.forEach((section) => section.questions.forEach((q) => q.statements.forEach((s) => counts[s.letter]++)));
    return counts;
  }, []);

  // Save results automatically when showing results
  useEffect(() => {
    if (showResults) {
      saveTestResult();
      try {
        const { letterScores, mbtiType, pairResults } = calculateResults;
        const testData = {
          scores: { letterScores, mbtiType, pairResults, personalityType: mbtiType },
          overallScore: Math.round((answeredQuestionsCount / totalQuestions) * 100),
          overallPercentage: Math.round((answeredQuestionsCount / totalQuestions) * 100),
          testType: 'mbti',
          completed: true
        };
        notifyTestCompletion('mbti', testData);
      } catch (e) {
        // no-op
      }
    }
  }, [showResults]);

  // Check for showResults URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showResultsParam = params.get('showResults');
    if (showResultsParam === 'true') {
      setShowResults(true);
    }
  }, [location.search]);

  const overallProgress = (answeredQuestionsCount / totalQuestions) * 100;
  const isCurrentQuestionComplete = useMemo(() => {
    if (!answers[currentQuestion.id]) return false;
    return Object.values(answers[currentQuestion.id]).some((v) => v === "selected");
  }, [answers, currentQuestion.id]);

  const handleNext = () => {
    if (currentQuestionIndex < currentCategory.questions.length - 1) {
      setCurrentQuestionIndex((p) => p + 1);
    } else if (currentSectionIndex < totalSections - 1) {
      setCurrentSectionIndex((p) => p + 1);
      setCurrentQuestionIndex(0);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((p) => p - 1);
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex((p) => p - 1);
      setCurrentQuestionIndex(mbtiSections[currentSectionIndex - 1].questions.length - 1);
    }
  };

  const calculateResults = useMemo(() => {
    const letterScores = { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 };

    mbtiSections.forEach((section) => {
      section.questions.forEach((question) => {
        question.statements.forEach((statement) => {
          const selectedOption = answers[question.id]?.[statement.id];
          if (selectedOption === "selected") letterScores[statement.letter] += 1;
        });
      });
    });

    const pairs = [
      { a: "E", b: "I", label: "Energy" },
      { a: "S", b: "N", label: "Information" },
      { a: "T", b: "F", label: "Decision" },
      { a: "J", b: "P", label: "Lifestyle" },
    ];

    const mbtiType = pairs
      .map(({ a, b }) => (letterScores[a] >= letterScores[b] ? a : b))
      .join("");

    const pairResults = pairs.map(({ a, b, label }) => {
      const aMax = maxScoreByLetter[a] || 1;
      const bMax = maxScoreByLetter[b] || 1;
      const aPct = Math.round((letterScores[a] / (aMax)) * 100);
      const bPct = Math.round((letterScores[b] / (bMax)) * 100);
      return { a, b, aScore: letterScores[a], bScore: letterScores[b], aPct, bPct, label };
    });

    return { letterScores, mbtiType, pairResults };
  }, [answers, maxScoreByLetter]);

  const saveTestResult = async () => {
    try {
      const { letterScores, mbtiType, pairResults } = calculateResults;
      const detailedAnswers = [];

      mbtiSections.forEach((category) => {
        category.questions.forEach((question) => {
          question.statements.forEach((statement) => {
            const selectedOption = answers[question.id]?.[statement.id];
            if (selectedOption === "selected") {
              detailedAnswers.push({
                questionId: question.id,
                questionText: question.prompt,
                statementId: statement.id,
                statementText: statement.text,
                selectedOptionId: statement.id,
                selectedOptionText: statement.text,
                letter: statement.letter,
              });
            }
          });
        });
      });
      await api.post("/api/test-results", {
        testType: "mbti",
        answers: detailedAnswers,
        scores: { letterScores, mbtiType, pairResults },
        overallPercentage: Math.round((answeredQuestionsCount / totalQuestions) * 100),
      });

      toast.success("MBTI Test results saved successfully!");
      setResultsSaved(true);
    } catch (error) {
      console.error("Error saving MBTI Test result:", error);
      toast.error(error.response?.data?.message || "Failed to save test results");
    }
  };

  const buildDetailedAnswers = () => {
    const detailedAnswers = [];
    mbtiSections.forEach((category) => {
      category.questions.forEach((question) => {
        question.statements.forEach((statement) => {
          const selectedOption = answers[question.id]?.[statement.id];
          if (selectedOption === "selected") {
            detailedAnswers.push({
              questionId: question.id,
              questionText: question.prompt,
              selectedOptionId: statement.id,
              selectedOptionText: statement.text
            });
          }
        });
      });
    });
    return detailedAnswers;
  };

  const fetchAIReport = async () => {
    try {
      setAiReportLoading(true);
      setAiReportError(null);
      const { letterScores, mbtiType, pairResults } = calculateResults;
      const detailedAnswers = buildDetailedAnswers();
      const payload = {
        testType: 'mbti',
        scores: { letterScores, mbtiType, pairResults },
        answers: detailedAnswers,
        overallPercentage: Math.round((answeredQuestionsCount / totalQuestions) * 100)
      };
      const { report } = await generateAIReport(payload);
      setAiReportText(report);
    } catch (err) {
      setAiReportError(err?.response?.data?.message || 'Failed to generate AI report');
    } finally {
      setAiReportLoading(false);
    }
  };

  const pairColor = (letter) => {
    switch (letter) {
      case "E":
      case "I":
        return "bg-blue-500";
      case "S":
      case "N":
        return "bg-emerald-500";
      case "T":
      case "F":
        return "bg-purple-500";
      default:
        return "bg-rose-500";
    }
  };

  const getDetailedReport = () => {
    const { mbtiType, pairResults } = calculateResults;

    // Comprehensive MBTI type descriptions
    const mbtiDescriptions = {
      "INTJ": {
        title: "The Architect",
        description: "Imaginative and strategic thinkers, with a plan for everything.",
        strengths: ["Strategic thinking", "Independent", "Determined", "Open-minded", "Honest and direct"],
        weaknesses: ["Overly analytical", "Perfectionist", "Impatient with incompetence", "May seem arrogant"],
        careerPaths: ["Strategic Planner", "Investment Banker", "Management Consultant", "Research Scientist", "Software Architect"],
        relationships: "INTJs value deep, meaningful connections and intellectual compatibility. They are loyal but need space and respect for their independence.",
        communication: "Prefer direct, logical communication. They appreciate efficiency and may seem blunt when focused on facts.",
        growthAreas: "Developing emotional intelligence, practicing patience, and learning to express feelings more openly."
      },
      "INTP": {
        title: "The Logician",
        description: "Innovative inventors with an unquenchable thirst for knowledge.",
        strengths: ["Analytical", "Original thinking", "Objective", "Abstract thinking", "Honest"],
        weaknesses: ["Can be insensitive", "May procrastinate", "Dislike rules", "Can be very private"],
        careerPaths: ["Software Developer", "Data Scientist", "Research Analyst", "Financial Analyst", "Systems Architect"],
        relationships: "INTPs seek intellectual stimulation and respect for their need for independence. They show love through problem-solving and sharing knowledge.",
        communication: "Prefer intellectual discussions and may seem detached when discussing emotions. They value clarity and logic.",
        growthAreas: "Developing emotional awareness, improving time management, and learning to express appreciation."
      },
      "ENTJ": {
        title: "The Commander",
        description: "Bold, imaginative and strong-willed leaders, always finding a way – or making one.",
        strengths: ["Efficient", "Energetic", "Self-confident", "Strong-willed", "Strategic thinking"],
        weaknesses: ["Impatient and intolerant", "May seem arrogant", "Poor handling of emotions", "May be insensitive"],
        careerPaths: ["Executive", "Entrepreneur", "Management Consultant", "Investment Banker", "Corporate Lawyer"],
        relationships: "ENTJs are loyal and committed partners who value intellectual compatibility. They show love through acts of service and planning.",
        communication: "Direct and efficient communicators who value honesty and clarity. They may need to practice active listening.",
        growthAreas: "Developing emotional intelligence, practicing patience, and learning to appreciate others' perspectives."
      },
      "ENTP": {
        title: "The Debater",
        description: "Smart and curious thinkers who cannot resist an intellectual challenge.",
        strengths: ["Knowledgeable", "Quick thinking", "Excellent brainstormers", "Charismatic", "Energetic"],
        weaknesses: ["Very argumentative", "May be insensitive", "Intolerant", "Can find it difficult to focus"],
        careerPaths: ["Entrepreneur", "Sales Representative", "Marketing Manager", "Political Consultant", "Innovation Manager"],
        relationships: "ENTPs are exciting and engaging partners who love intellectual stimulation. They need variety and may struggle with routine.",
        communication: "Engaging and persuasive communicators who love debate. They may need to practice listening without immediately responding.",
        growthAreas: "Developing follow-through, practicing empathy, and learning to appreciate routine and stability."
      },
      "INFJ": {
        title: "The Advocate",
        description: "Quiet and mystical, yet very inspiring and tireless idealists.",
        strengths: ["Creative", "Insightful", "Inspiring and convincing", "Decisive", "Determined and passionate"],
        weaknesses: ["Sensitive to criticism", "May be perfectionists", "Can burn out easily", "May be overly idealistic"],
        careerPaths: ["Counselor", "Writer", "Teacher", "Human Resources Manager", "Social Worker"],
        relationships: "INFJs are deeply committed and empathetic partners who seek meaningful connections. They need understanding and space to process emotions.",
        communication: "Thoughtful and empathetic communicators who value authenticity. They may need encouragement to express their needs.",
        growthAreas: "Setting boundaries, practicing self-care, and learning to accept imperfection."
      },
      "INFP": {
        title: "The Mediator",
        description: "Poetic, kind and altruistic people, always eager to help a good cause.",
        strengths: ["Idealistic", "Seek and value harmony", "Open-minded and flexible", "Very creative and artistic", "Loyal and devoted"],
        weaknesses: ["Too idealistic", "Too altruistic", "Impractical", "Emotionally vulnerable", "May be too selfless"],
        careerPaths: ["Writer", "Artist", "Counselor", "Teacher", "Social Worker"],
        relationships: "INFPs are romantic and idealistic partners who value deep emotional connections. They need understanding and appreciation for their unique perspective.",
        communication: "Gentle and empathetic communicators who value emotional authenticity. They may need time to process before responding.",
        growthAreas: "Developing practical skills, setting boundaries, and learning to balance idealism with reality."
      },
      "ENFJ": {
        title: "The Protagonist",
        description: "Charismatic and inspiring leaders, able to mesmerize their listeners.",
        strengths: ["Tolerant", "Reliable", "Charismatic", "Altruistic", "Natural leaders"],
        weaknesses: ["Overly idealistic", "Too selfless", "Too sensitive", "Fluctuating self-esteem", "Struggle to make tough decisions"],
        careerPaths: ["Teacher", "Human Resources Manager", "Sales Manager", "Public Relations Specialist", "Non-profit Director"],
        relationships: "ENFJs are nurturing and devoted partners who prioritize their loved ones' happiness. They need appreciation and emotional support.",
        communication: "Warm and inspiring communicators who naturally motivate others. They may need to practice receiving feedback.",
        growthAreas: "Developing self-care, setting boundaries, and learning to prioritize their own needs."
      },
      "ENFP": {
        title: "The Campaigner",
        description: "Enthusiastic, creative and sociable free spirits, who can always find a reason to smile.",
        strengths: ["Curious and observant", "Energetic and enthusiastic", "Excellent communicators", "Very popular and friendly", "Natural motivators"],
        weaknesses: ["Poor practical skills", "Find it difficult to focus", "Overthink things", "Get stressed easily", "Highly emotional"],
        careerPaths: ["Journalist", "Actor", "Teacher", "Sales Representative", "Event Planner"],
        relationships: "ENFPs are passionate and enthusiastic partners who bring excitement and creativity. They need variety and emotional support.",
        communication: "Expressive and engaging communicators who love sharing ideas. They may need to practice active listening.",
        growthAreas: "Developing focus and follow-through, managing emotions, and learning practical skills."
      },
      "ISTJ": {
        title: "The Logistician",
        description: "Practical and fact-minded individuals, whose reliability cannot be doubted.",
        strengths: "Honest and direct, Strong-willed and dutiful, Very responsible and calm, Practical and realistic, Create and enforce order",
        weaknesses: "Stubborn, Insensitive, Always by the book, Judgmental, Have difficulty expressing emotion",
        careerPaths: ["Accountant", "Military Officer", "Police Officer", "Project Manager", "Quality Control Specialist"],
        relationships: "ISTJs are loyal and reliable partners who value stability and commitment. They show love through acts of service and dependability.",
        communication: "Direct and practical communicators who value clarity and honesty. They may need to practice emotional expression.",
        growthAreas: "Developing flexibility, expressing emotions, and learning to appreciate spontaneity."
      },
      "ISFJ": {
        title: "The Defender",
        description: "Very dedicated and warm protectors, always ready to defend their loved ones.",
        strengths: "Supportive, Reliable, Patient, Imaginative, Observant, Enthusiastic, Loyal, Hard-working",
        weaknesses: "Humble and shy, Take things too personally, Repress their feelings, Overload themselves, Reluctant to change, Too altruistic",
        careerPaths: ["Nurse", "Teacher", "Social Worker", "Administrative Assistant", "Customer Service Representative"],
        relationships: "ISFJs are nurturing and devoted partners who prioritize their loved ones' well-being. They need appreciation and emotional support.",
        communication: "Gentle and supportive communicators who value harmony. They may need encouragement to express their own needs.",
        growthAreas: "Setting boundaries, expressing needs, and learning to accept change."
      },
      "ESTJ": {
        title: "The Executive",
        description: "Excellent administrators, unsurpassed at managing things or people.",
        strengths: "Dedicated, Strong-willed, Direct and honest, Loyal and patient, Enjoy creating order, Excellent organizational skills",
        weaknesses: "Inflexible and stubborn, Uncomfortable with unconventional situations, Judgmental, Too focused on social status, Difficult to relax",
        careerPaths: ["Business Manager", "Military Officer", "Police Officer", "Project Manager", "Financial Advisor"],
        relationships: "ESTJs are loyal and responsible partners who value stability and commitment. They show love through practical support and reliability.",
        communication: "Direct and efficient communicators who value clarity and honesty. They may need to practice emotional sensitivity.",
        growthAreas: "Developing flexibility, emotional intelligence, and learning to appreciate unconventional approaches."
      },
      "ESFJ": {
        title: "The Consul",
        description: "Extraordinarily caring, social and popular people, for whom it is important to be in harmony with others.",
        strengths: "Sensitive and warm, Very social, Popular and friendly, Reliable and patient, Practical and dependable",
        weaknesses: "Worry too much about what others think, Overly sensitive, Have difficulty accepting criticism, Too needy, Too selfless",
        careerPaths: "Nurse, Teacher, Human Resources Manager, Sales Representative, Customer Service Manager",
        relationships: "ESFJs are caring and devoted partners who prioritize harmony and their loved ones' happiness. They need appreciation and emotional support.",
        communication: "Warm and supportive communicators who value harmony and connection. They may need to practice receiving feedback.",
        growthAreas: "Setting boundaries, managing criticism, and learning to prioritize their own needs."
      },
      "ISTP": {
        title: "The Virtuoso",
        description: "Bold and practical experimenters, masters of all kinds of tools.",
        strengths: "Optimistic and energetic, Creative, practical and charming, Spontaneous and rational, Know how to prioritize, Great in a crisis",
        weaknesses: "Stubborn, Insensitive, Private and reserved, Easily bored, Dislike commitment, Risky behavior",
        careerPaths: ["Mechanic", "Pilot", "Engineer", "Athlete", "Police Officer"],
        relationships: "ISTPs are independent and practical partners who value freedom and authenticity. They show love through acts of service and problem-solving.",
        communication: "Direct and practical communicators who value efficiency. They may need to practice emotional expression and commitment.",
        growthAreas: "Developing emotional intelligence, practicing commitment, and learning to express feelings."
      },
      "ISFP": {
        title: "The Adventurer",
        description: "Flexible and charming artists, always ready to explore and experience something new.",
        strengths: "Charming, Sensitive to others, Imaginative, Passionate, Curious, Artistic",
        weaknesses: "Fiercely independent, Unpredictable, Easily stressed, Overly competitive, Function in the moment",
        careerPaths: ["Artist", "Designer", "Photographer", "Veterinarian", "Physical Therapist"],
        relationships: "ISFPs are passionate and authentic partners who value freedom and emotional connection. They need space and understanding.",
        communication: "Gentle and authentic communicators who value emotional honesty. They may need time to process before responding.",
        growthAreas: "Developing long-term planning, managing stress, and learning to balance independence with connection."
      },
      "ESTP": {
        title: "The Entrepreneur",
        description: "Smart, energetic and very perceptive people, who truly enjoy living on the edge.",
        strengths: "Bold, Rational and practical, Direct, Sociable, Spontaneous, Excellent at managing crises",
        weaknesses: "Impatient, Risk-takers, Unstructured, May miss the bigger picture, Insensitive, May be rebellious",
        careerPaths: ["Entrepreneur", "Sales Representative", "Athlete", "Police Officer", "Emergency Responder"],
        relationships: "ESTPs are exciting and adventurous partners who bring energy and spontaneity. They need variety and may struggle with routine.",
        communication: "Direct and energetic communicators who value action and results. They may need to practice patience and emotional sensitivity.",
        growthAreas: "Developing patience, long-term thinking, and emotional intelligence."
      },
      "ESFP": {
        title: "The Entertainer",
        description: "Spontaneous, energetic and enthusiastic entertainers – life is never boring around them.",
        strengths: "Bold, Original, Practical, Excellent people skills, Natural performers, Generous",
        weaknesses: "Sensitive, Conflict-averse, Easily bored, Focus on immediate gratification, Poor long-term planners",
        careerPaths: ["Actor", "Sales Representative", "Event Planner", "Tour Guide", "Customer Service Representative"],
        relationships: "ESFPs are fun-loving and generous partners who bring excitement and joy. They need variety and emotional support.",
        communication: "Enthusiastic and engaging communicators who love to entertain. They may need to practice listening and long-term thinking.",
        growthAreas: "Developing focus, long-term planning, and learning to handle conflict constructively."
      }
    };

    const typeInfo = mbtiDescriptions[mbtiType] || {
      title: "Your MBTI Type",
      description: "A unique combination of personality preferences that shapes how you see and interact with the world.",
      strengths: ["Individual strengths will be displayed here"],
      weaknesses: ["Areas for growth will be displayed here"],
      careerPaths: ["Career suggestions will be displayed here"],
      relationships: "Relationship insights will be displayed here",
      communication: "Communication style will be displayed here",
      growthAreas: "Personal development areas will be displayed here"
    };

    return (
      <div className="space-y-6">
        {/* MBTI Type Overview */}
        <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-purple-800 flex items-center gap-3">
              <Sparkles className="w-6 h-6" />
              {mbtiType} - {typeInfo.title}
            </CardTitle>
            <CardDescription className="text-purple-700 text-lg">
              {typeInfo.description}
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Detailed Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Strengths */}
          <Card className="bg-green-50 border-green-200">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-green-800 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Your Strengths
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {Array.isArray(typeInfo.strengths) ?
                  typeInfo.strengths.map((strength, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-green-700">{strength}</span>
                    </li>
                  )) :
                  <li className="text-green-700">{typeInfo.strengths}</li>
                }
              </ul>
            </CardContent>
          </Card>

          {/* Growth Areas */}
          <Card className="bg-orange-50 border-orange-200">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-orange-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Areas for Growth
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {Array.isArray(typeInfo.weaknesses) ?
                  typeInfo.weaknesses.map((weakness, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      <span className="text-orange-700">{weakness}</span>
                    </li>
                  )) :
                  <li className="text-orange-700">{typeInfo.weaknesses}</li>
                }
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Career Guidance */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-blue-800 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Career Paths
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Array.isArray(typeInfo.careerPaths) ?
                typeInfo.careerPaths.map((career, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border border-blue-200">
                    <span className="text-blue-700 font-medium">{career}</span>
                  </div>
                )) :
                <div className="bg-white rounded-lg p-3 border border-blue-200">
                  <span className="text-blue-700">{typeInfo.careerPaths}</span>
                </div>
              }
            </div>
          </CardContent>
        </Card>

        {/* Relationships & Communication */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-pink-50 border-pink-200">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-pink-800 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Relationships
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-pink-700 leading-relaxed">{typeInfo.relationships}</p>
            </CardContent>
          </Card>

          <Card className="bg-cyan-50 border-cyan-200">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-cyan-800 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Communication Style
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-cyan-700 leading-relaxed">{typeInfo.communication}</p>
            </CardContent>
          </Card>
        </div>

        {/* Personal Development */}
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-emerald-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Personal Development
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-emerald-700 leading-relaxed">{typeInfo.growthAreas}</p>
          </CardContent>
        </Card>

        {/* MBTI Preferences Breakdown */}
        <Card className="bg-gray-50 border-gray-200">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Your MBTI Preferences Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pairResults.map((pair, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{pair.label}</span>
                    <span className="text-sm text-gray-500">
                      {pair.a}: {pair.aScore} | {pair.b}: {pair.bScore}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${pair.aPct}%` }}
                      />
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-green-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${pair.bPct}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{pair.a} ({pair.aPct}%)</span>
                    <span>{pair.b} ({pair.bPct}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (showIntro) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        {/* Sidebar */}
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>

        {/* Main content */}
        <div className="ml-64">
          <Navbar />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <Card className="text-center bg-white shadow-lg">
                <CardHeader className="space-y-4">
                  <CardTitle className="text-3xl font-bold">
                    Myers-Briggs Personality Test (MBTI)
                  </CardTitle>
                  <CardDescription className="text-lg text-gray-600">
                    Identify your personality type across the 4 MBTI dimensions
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    <div className="space-y-2">
                      <h3 className="font-semibold text-blue-600">Extraversion</h3>
                      <p className="text-sm text-gray-600">
                        Extraversion vs. Introversion – how you gain energy
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-green-600">Sensing</h3>
                      <p className="text-sm text-gray-600">
                        Sensing vs. Intuition – how you take in information
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-purple-600">Thinking (T) / Feeling (F)
                      </h3>
                      <p className="text-sm text-gray-600">
                        Thinking vs. Feeling – how you make decisions
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-rose-600">Judging</h3>
                      <p className="text-sm text-gray-600">
                        Judging vs. Perceiving – your lifestyle preferences
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-rose-600">Lifestyle</h3>
                      <p className="text-sm text-gray-600">
                        lifestyle preferences
                      </p>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>{mbtiSections.reduce((a, s) => a + s.questions.length, 0)} questions</strong> • Gain insights into your MBTI personality type
                    </p>
                  </div>

                  <Button
                    onClick={() => {
                      // Mark free test as used when user actually starts the test
                      try {
                        const userStr = localStorage.getItem('user');
                        const user = userStr ? JSON.parse(userStr) : null;
                        const isPremium = user && (user.role === 'premium' || user.role === 'admin');
                        if (!isPremium) {
                          localStorage.setItem('freeTestUsed', '1');
                        }
                      } catch (_) {}
                      setShowIntro(false);
                    }}
                    className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                    size="lg"
                  >
                    Start Test <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results views
  if (showResults) {
    const { mbtiType, pairResults } = calculateResults;

    if (showReport) {
      return (
        <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: "linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)" }}>
          <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
            <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
          </div>
          <div className="ml-0 md:ml-64 min-h-screen">
            <Navbar toggleSidebar={toggleSidebar} />
            <div className="min-h-screen p-4">
              <div className="mx-auto space-y-6" style={{ width: "85%" }}>
                <Card className="bg-[#ffffff]">
                  <CardHeader>
                    <CardTitle className="text-2xl font-bold flex items-center">
                      <FileText className="mr-3 w-6 h-6" /> Detailed MBTI Report
                    </CardTitle>
                    <CardDescription>Breakdown of your preferences across each MBTI dimension</CardDescription>
                  </CardHeader>
                </Card>

                {getDetailedReport()}

                <Card className="bg-[#ffffff]">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold">We are collecting your results</CardTitle>
                    <CardDescription>Personalized insights</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {aiReportLoading && (
                      <div className="flex items-center text-gray-600">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                        Generating your report...
                      </div>
                    )}
                    {aiReportError && (
                      <div className="text-red-600 text-sm mb-2">{aiReportError}</div>
                    )}
                    {!aiReportLoading && aiReportText && (
                      <div className="prose max-w-none whitespace-pre-wrap text-gray-800 text-sm">{aiReportText}</div>
                    )}
                    {!aiReportLoading && !aiReportText && (
                      <Button onClick={fetchAIReport} className="bg-blue-600 hover:bg-blue-700 text-white">Generate Report</Button>
                    )}
                  </CardContent>
                </Card>

                <div className="flex gap-4">
                  <Button onClick={() => setShowReport(false)} variant="outline" className="flex-1 bg-[#ffffff]">
                    Back to Results
                  </Button>
                  <Button
                    onClick={() => {
                      setShowResults(false);
                      setShowReport(false);
                      setCurrentSectionIndex(0);
                      setCurrentQuestionIndex(0);
                      setAnswers({});
                      setResultsSaved(false);
                    }}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Retake Test
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: "linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)" }}>
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        </div>
        <div className="ml-0 md:ml-64 min-h-screen">
          <Navbar toggleSidebar={toggleSidebar} />

          <div className="min-h-screen p-4">
            <div className="mx-auto" style={{ width: "85%" }}>
              <Card className="bg-[#ffffff]">
                <CardHeader className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <Brain className="w-8 h-8 text-purple-600" />
                  </div>
                  <CardTitle className="text-3xl font-bold">Your MBTI Results</CardTitle>
                  <CardDescription>Based on the Myers-Briggs Type Indicator framework</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <div className="text-sm text-gray-600">Your type</div>
                    <div className="text-4xl font-extrabold mt-1">{mbtiType}</div>
                  </div>

                  {pairResults.map((p) => (
                    <div key={p.label} className="space-y-3">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-lg">{p.label}: {p.a}/{p.b}</h3>
                        <div className="text-right">
                          <span className="font-bold">{p.aScore}/{maxScoreByLetter[p.a]} • {p.bScore}/{maxScoreByLetter[p.b]}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1"><span>{p.a}</span><span>{p.aPct}%</span></div>
                          <Progress value={p.aPct} className="h-3" />
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1"><span>{p.b}</span><span>{p.bPct}%</span></div>
                          <Progress value={p.bPct} className="h-3" />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-4 pt-6">
                    <Button onClick={() => setShowReport(true)} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white">
                      <FileText className="mr-2 w-4 h-4" /> View Detailed Report
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowResults(false);
                        setShowReport(false);
                        setCurrentSectionIndex(0);
                        setCurrentQuestionIndex(0);
                        setAnswers({});
                        setResultsSaved(false);
                      }}
                    >
                      <RotateCcw className="mr-2 w-4 h-4" /> Retake Test
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quiz view
  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: "linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)" }}>
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>
      <div className="ml-0 md:ml-64 min-h-screen ">
        <Navbar toggleSidebar={toggleSidebar} />
        <div className="mx-auto" style={{ width: "85%" }}>
          {/* Header */}
          <div className="text-center mb-8 mt-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Myers-Briggs Personality Test</h1>
            <p className="text-xl text-gray-600">Identify your 4-letter MBTI type across the four core dimensions</p>
          </div>

          {/* Progress by Section */}
          <Card className="rounded-lg border text-card-foreground shadow-sm mb-6 bg-[#ffffff] ">
            <CardHeader>
              <CardTitle className="text-lg">Your Progress by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4 mb-6">
                {mbtiSections.map((section, index) => {
                  const Icon = section.icon;
                  return (
                    <div key={section.name} className="text-center">
                      <div className={`w-12 h-12 mx-auto mb-2 rounded-full ${section.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${section.textColor}`} />
                      </div>
                      <h3 className="font-medium text-sm mb-1">{section.name}</h3>
                      <p className="text-xs text-gray-500">{sectionScores[index]}/5</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Overall progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600">Question {currentSectionIndex * 5 + currentQuestionIndex + 1} of {totalQuestions}</span>
              <span className="text-sm text-gray-600">{Math.round(overallProgress)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-purple-600 h-2 rounded-full transition-all duration-300" style={{ width: `${overallProgress}%` }} />
            </div>
          </div>

          {/* Question Card */}
          <Card className="rounded-lg border text-card-foreground shadow-sm bg-[#ffffff]">
            <CardHeader className="pb-0 border-b">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <CardTitle className="text-xl mb-2">{currentSectionIndex + 1}.{currentQuestionIndex + 1} {currentCategory.name}</CardTitle>
                  <p className="text-sm text-gray-500 mb-4">{currentCategory.description}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <h3 className="text-xl font-semibold mb-6 text-gray-900 leading-relaxed mt-3">{currentQuestion.prompt}</h3>

              <div className="space-y-3">
                {currentQuestion.statements.map((statement) => {
                  const isSelected = answers[currentQuestion.id]?.[statement.id] === "selected";
                  return (
                    <div
                      key={statement.id}
                      onClick={() => handleAnswerChange(currentQuestion.id, statement.id, "selected")}
                      className={`flex items-center rounded-xl border-2 p-3 px-4 cursor-pointer transition-all duration-200 ${isSelected ? "border-primary ring-2 ring-primary/50 bg-primary/5" : "hover:bg-gray-50"
                        }`}
                    >
                      <div className="flex items-center gap-4 w-full">
                        <div className="flex-1">
                          <div >{statement.text}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between items-center m-5">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              className="bg-[#ffffff] px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none border border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
            >
              Previous
            </Button>

            {/* <Button onClick={handleNext} disabled={!isCurrentQuestionComplete} className={`bg-purple-600 hover:bg-purple-700 text-white flex items-center space-x-2`}>
              {isLastQuestion ? "View Results" : canGoNext ? "Next Question" : "Next Section"}
            </Button> */}
          </div>
        </div>
      </div>
    </div>
  );
}
